import {
  AlertTriangle,
  Check,
  Info,
  Trash2,
} from 'lucide-react-native';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

/**
 * Custom confirmation dialog with a slight pop animation. Replaces every
 * `window.confirm` / native `Alert.alert` call — those break the visual
 * language (and on web actually leak the underlying URL).
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete?",
 *     message: "This can't be undone.",
 *     destructive: true,
 *     confirmLabel: "Delete",
 *   });
 *   if (ok) { ... }
 */

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  tone?: 'neutral' | 'warning' | 'destructive' | 'info' | 'error';
  /** Single-button acknowledgement (replaces native alert()). */
  singleButton?: boolean;
}

export interface NoticeOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  tone?: 'neutral' | 'warning' | 'info' | 'error';
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

interface ConfirmCtx {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const Ctx = createContext<ConfirmCtx>({
  confirm: async () => false,
});

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      const next: PendingConfirm = { ...opts, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const finish = useCallback((ok: boolean) => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    p?.resolve(ok);
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <DialogView pending={pending} onFinish={finish} />
    </Ctx.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  return useContext(Ctx).confirm;
}

/**
 * Single-button acknowledgement (drop-in for Alert.alert / window.alert).
 * Always resolves, so callers don't need to branch on the result.
 */
export function useNotice(): (opts: NoticeOptions) => Promise<void> {
  const { confirm } = useContext(Ctx);
  return async (opts: NoticeOptions) => {
    await confirm({
      title: opts.title,
      message: opts.message,
      confirmLabel: opts.confirmLabel ?? 'OK',
      tone: opts.tone ?? 'neutral',
      singleButton: true,
    });
  };
}

function DialogView({
  pending,
  onFinish,
}: {
  pending: PendingConfirm | null;
  onFinish: (ok: boolean) => void;
}) {
  const open = pending !== null;
  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.9);

  useEffect(() => {
    if (open) {
      opacity.value = withTiming(1, { duration: 180, easing: Easing.out(Easing.cubic) });
      scale.value = withSpring(1, { mass: 0.6, damping: 14, stiffness: 220 });
    } else {
      opacity.value = withTiming(0, { duration: 120 });
      scale.value = withTiming(0.95, { duration: 120 });
    }
  }, [open, opacity, scale]);

  const backdropStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));
  const cardStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  if (!pending) return null;

  const tone = pending.destructive ? 'destructive' : pending.tone ?? 'neutral';
  const Icon =
    tone === 'destructive' || tone === 'error'
      ? tone === 'error' ? AlertTriangle : Trash2
      : tone === 'warning'
      ? AlertTriangle
      : tone === 'info'
      ? Info
      : Check;
  const iconBg =
    tone === 'destructive' || tone === 'error'
      ? 'rgba(176,95,60,0.15)'
      : tone === 'warning'
      ? 'rgba(225,163,61,0.18)'
      : tone === 'info'
      ? 'rgba(92,126,85,0.15)'
      : '#EAF0EA';
  const iconColor =
    tone === 'destructive' || tone === 'error'
      ? Palette.clay
      : tone === 'warning'
      ? Palette.honeyDeep
      : Palette.forest;
  const singleButton = pending.singleButton === true;

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={() => onFinish(false)}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={styles.backdropPress} onPress={() => onFinish(false)} />
      </Animated.View>
      <View pointerEvents="box-none" style={styles.wrap}>
        <Animated.View style={[styles.card, cardStyle]}>
          <View style={[styles.iconCircle, { backgroundColor: iconBg }]}>
            <Icon size={26} color={iconColor} strokeWidth={1.8} />
          </View>
          <Text style={styles.title}>{pending.title}</Text>
          {pending.message ? <Text style={styles.message}>{pending.message}</Text> : null}
          <View style={styles.actions}>
            {singleButton ? null : (
              <Pressable
                onPress={() => onFinish(false)}
                style={({ pressed }) => [
                  styles.btn,
                  styles.btnCancel,
                  pressed && { opacity: 0.85 },
                ]}>
                <Text style={styles.btnCancelText}>{pending.cancelLabel ?? 'Cancel'}</Text>
              </Pressable>
            )}
            <Pressable
              onPress={() => onFinish(true)}
              style={({ pressed }) => [
                styles.btn,
                tone === 'destructive' ? styles.btnDestructive : styles.btnConfirm,
                pressed && { transform: [{ scale: 0.97 }] },
              ]}>
              <Text
                style={
                  tone === 'destructive' ? styles.btnDestructiveText : styles.btnConfirmText
                }>
                {pending.confirmLabel ?? 'Confirm'}
              </Text>
            </Pressable>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 24, 18, 0.6)',
  },
  backdropPress: { flex: 1 },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: Palette.sand,
    borderRadius: 24,
    paddingHorizontal: 24,
    paddingTop: 28,
    paddingBottom: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 10 },
    elevation: 18,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 21,
    color: Palette.ink,
    letterSpacing: -0.3,
    textAlign: 'center',
  },
  message: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 20,
    paddingHorizontal: 4,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 22,
    width: '100%',
  },
  btn: {
    flex: 1,
    paddingVertical: 13,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnCancel: {
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  btnCancelText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.inkSoft,
  },
  btnConfirm: {
    backgroundColor: Palette.forest,
  },
  btnConfirmText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
  },
  btnDestructive: {
    backgroundColor: Palette.clay,
  },
  btnDestructiveText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
  },
});
